import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Paperclip, X } from "lucide-react";

/**
 * Public page a homeowner lands on when they click "Reply in the HOA portal →"
 * inside an admin's email. The token in the URL is a signed JWT that ties the
 * incoming reply back to the original contact_messages row — no login required.
 */
export default function HomeownerReplyPage() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [attachments, setAttachments] = useState([]);   // {storage_path, filename, content_type, size, url}
  const [uploading, setUploading] = useState(false);

  const MAX_ATTACHMENTS = 3;
  const MAX_SIZE_MB = 10;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const slots = MAX_ATTACHMENTS - attachments.length;
    if (slots <= 0) { toast.error(`Max ${MAX_ATTACHMENTS} attachments`); return; }
    const picked = Array.from(files).slice(0, slots);
    setUploading(true);
    try {
      for (const f of picked) {
        if (f.size > MAX_SIZE_MB * 1024 * 1024) { toast.error(`${f.name} exceeds ${MAX_SIZE_MB} MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        try {
          const r = await api.post(`/portal/reply/${token}/attach`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          setAttachments((a) => [...a, r.data]);
        } catch (e) { toast.error(`${f.name}: ${e.response?.data?.detail || "Upload failed"}`); }
      }
    } finally { setUploading(false); }
  };

  const removeAttachment = (idx) => setAttachments((a) => a.filter((_, i) => i !== idx));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/portal/reply/${token}`);
        if (!cancelled) setCtx(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "This reply link is not valid.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const send = async () => {
    if (!body.trim()) { toast.error("Please write a message first"); return; }
    setSending(true);
    try {
      await api.post(`/portal/reply/${token}`, { body, attachments });
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="homeowner-reply-page">
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Homeowners Association</p>
      <h1 className="font-serif text-4xl text-[#1A2421] mb-8">Reply to the board</h1>

      {loading && <p className="text-[#5C6A64]">Loading…</p>}
      {!loading && error && (
        <Card className="rounded-2xl border-[#C86650]" data-testid="reply-error">
          <CardContent className="p-6">
            <p className="text-[#C86650] font-medium mb-1">Reply link problem</p>
            <p className="text-sm text-[#5C6A64]">{error}</p>
          </CardContent>
        </Card>
      )}
      {!loading && !error && sent && (
        <Card className="rounded-2xl border-[#2C4C3B]" data-testid="reply-sent">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-[#2C4C3B] mx-auto mb-3" />
            <p className="text-[#1A2421] font-medium mb-1">Reply sent to the board</p>
            <p className="text-sm text-[#5C6A64]">A board member will see it in the admin console and follow up with you soon.</p>
          </CardContent>
        </Card>
      )}
      {!loading && !error && !sent && ctx && (
        <>
          {ctx.last_admin_reply && (
            <Card className="rounded-2xl border-[#D5CFC4] bg-[#F2EFE8] mb-6" data-testid="last-admin-reply">
              <CardContent className="p-5">
                <p className="text-xs text-[#5C6A64] mb-2 flex items-center gap-2">
                  <MessageCircle className="w-3 h-3" />
                  <b className="text-[#2C4C3B]">Board wrote:</b>
                  <span>{new Date(ctx.last_admin_reply.sent_at).toLocaleString()}</span>
                </p>
                <p className="text-sm text-[#1A2421] whitespace-pre-wrap">{ctx.last_admin_reply.body}</p>
              </CardContent>
            </Card>
          )}
          <Card className="rounded-2xl border-[#D5CFC4]">
            <CardContent className="p-6">
              <p className="text-xs text-[#5C6A64] mb-3">
                Replying as <b className="text-[#1A2421]">{ctx.homeowner_email}</b>{ctx.subject ? <> · on: {ctx.subject}</> : null}
              </p>
              <Textarea
                data-testid="homeowner-reply-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                placeholder="Type your reply to the board…"
                className="bg-white border-[#D5CFC4]"
              />
              {attachments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="attachments-list">
                  {attachments.map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2 rounded-full bg-[#EADCC8] border border-[#D5CFC4] pl-3 pr-1 py-1 text-xs text-[#1A2421]" data-testid={`attachment-chip-${idx}`}>
                      <Paperclip className="w-3 h-3 text-[#2C4C3B]" />
                      <span className="max-w-[16rem] truncate">{a.filename}</span>
                      <span className="text-[#5C6A64]">· {(a.size / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        data-testid={`attachment-remove-${idx}`}
                        onClick={() => removeAttachment(idx)}
                        className="p-1 rounded-full hover:bg-[#D5CFC4]"
                        aria-label="Remove attachment"
                      >
                        <X className="w-3 h-3 text-[#5C6A64]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between mt-4">
                <label className="inline-flex items-center gap-2 text-sm text-[#2C4C3B] cursor-pointer" data-testid="attach-file-label">
                  <input
                    type="file"
                    className="hidden"
                    data-testid="attach-file-input"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
                    multiple
                    disabled={uploading || attachments.length >= MAX_ATTACHMENTS}
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                  />
                  <span className={`inline-flex items-center gap-1.5 rounded-full border border-[#D5CFC4] px-3 py-1.5 hover:bg-[#F2EFE8] ${uploading || attachments.length >= MAX_ATTACHMENTS ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <Paperclip className="w-4 h-4" />
                    {uploading ? "Uploading…" : `Attach photo or PDF (${attachments.length}/${MAX_ATTACHMENTS})`}
                  </span>
                </label>
                <Button
                  data-testid="homeowner-reply-send"
                  onClick={send}
                  disabled={sending || uploading || !body.trim()}
                  className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white px-6"
                >
                  {sending ? "Sending…" : "Send reply"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
